/**
 * Central SEO metadata for every content route, used by the TanStack Start
 * route `head()` option so titles/descriptions/canonicals are correct in the
 * server-rendered HTML (react-helmet-async only runs on the client).
 */

export type SeoLang = "en" | "zh" | "th";

type Entry = {
  en: { title: string; description: string };
  zh: { title: string; description: string };
  th: { title: string; description: string };
  ogType?: string;
};

const SITE = "https://cargonova.ai";

export const PAGE_SEO: Record<string, Entry> = {
  "/": {
    en: {
      title: "CargoNova AI | AI-Native Freight Forwarding Software",
      description:
        "CargoNova AI is an AI-native freight forwarding software platform by Deep Insights. AI agents automate quoting, booking, documents, customs and settlement.",
    },
    zh: {
      title: "CargoNova AI 官网 | AI 原生国际货代系统 — 报价 订舱 单证",
      description:
        "CargoNova AI 是 Deep Insights 旗下 AI Native 国际货代系统，AI Agent 自动完成报价、订舱、单证、清关与结算，服务海运与空运全球物流。",
    },
    th: {
      title: "CargoNova AI | ซอฟต์แวร์ฟอร์เวิร์ดเดอร์ AI-Native",
      description:
        "CargoNova AI คือแพลตฟอร์มซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบ AI-Native โดย Deep Insights มี AI Agent ทำใบเสนอราคา จองระวาง เอกสาร พิธีการศุลกากร และการชำระเงิน",
    },

  },
  "/about": {
    en: {
      title: "About CargoNova — AI Freight Platform by Deep Insights",
      description:
        "CargoNova is an AI freight platform by Deep Insights (Singapore). Learn about our mission to transform global freight forwarding with AI-native technology.",
    },
    zh: {
      title: "关于 CargoNova — Deep Insights 旗下 AI 货代平台",
      description:
        "CargoNova 是 Deep Insights（新加坡）旗下的 AI 原生国际货代平台。了解我们的使命、团队与发展历程：如何用 AI Agent 与云原生架构重构报价、订舱、单证、关务与结算流程，帮助东南亚与中国出海货代企业实现数字化转型与降本增效。",
    },

    th: {
      title: "เกี่ยวกับ CargoNova — แพลตฟอร์ม AI จาก Deep Insights",
      description:
        "CargoNova คือแพลตฟอร์มฟอร์เวิร์ดเดอร์ AI จาก Deep Insights (สิงคโปร์) รู้จักพันธกิจของเราในการเปลี่ยนอุตสาหกรรมขนส่งด้วยเทคโนโลยี AI-Native",
    },
  },
  "/solutions": {
    en: {
      title: "Freight Forwarding System & Platform | CargoNova",
      description:
        "AI-native freight forwarding system and freight forwarder platform for air, ocean and multimodal operators — digital forwarding software that cuts costs 25%+.",
    },
    zh: {
      title: "货代系统与数字货代平台：海运 空运 多式联运 | CargoNova",
      description:
        "CargoNova 提供面向海运、空运与多式联运的货代系统与数字货代平台（freight forwarding system / platform），以国际货代 AI 系统能力覆盖运价、报价、订舱、单证、关务与结算全流程，服务中小型到全球型智能货运代理，帮助企业降低 25%+ 物流运营成本。",
    },

    th: {
      title: "ระบบและแพลตฟอร์ม Freight Forwarder | CargoNova",
      description:
        "ระบบ freight forwarder และแพลตฟอร์มฟอร์เวิร์ดเดอร์ดิจิทัลสำหรับการขนส่งทางอากาศ ทางทะเล และมัลติโมดัล ซอฟต์แวร์ AI-Native ลดต้นทุนกว่า 25%",
    },

  },

  "/customers": {
    en: {
      title: "Freight Case Studies & Success | CargoNova",
      description:
        "Real case studies from freight forwarders across SE Asia. See how CargoNova customers achieve 99.5% on-time delivery and 30% faster turnaround.",
    },
    zh: {
      title: "货代客户案例与成果 | CargoNova",
      description:
        "查看东南亚与中国出海货代企业使用 CargoNova 国际货代 AI 系统的真实客户案例：智能报价、自动订舱、单证识别与费用结算如何落地，客户实现 99.5% 准时交付、30% 作业提效与显著的人力成本节省。",
    },

    th: {
      title: "กรณีศึกษาลูกค้าและผลลัพธ์ | CargoNova",
      description:
        "กรณีศึกษาจริงจากฟอร์เวิร์ดเดอร์ทั่วเอเชียตะวันออกเฉียงใต้ ดูว่าลูกค้า CargoNova ทำ on-time delivery 99.5% และเร็วขึ้น 30% ได้อย่างไร",
    },
  },
  "/digital-workers": {
    en: {
      title: "Freight Forwarding AI Agents | CargoNova Digital Workers",
      description:
        "Eight freight forwarding AI agents — NovaRate, NovaSales, NovaQuote, NovaBook and more — automate rates, quoting, booking, document OCR and settlement.",
    },
    zh: {
      title: "货代 AI 智能体 / AI 数字员工 | CargoNova",
      description:
        "CargoNova 提供 8 个货代 AI 智能体（NovaRate、NovaSales、NovaQuote、NovaBook 等 AI 数字员工），自动完成运价获取、智能报价、订舱、单证 OCR 识别、关务与费用结算，构成完整的货代报价系统与 AI 数字员工矩阵，全天候替代重复性人工作业。",
    },

    th: {
      title: "AI Agent สำหรับฟอร์เวิร์ดเดอร์ | CargoNova Digital Workers",
      description:
        "AI Agent 8 ตัว (NovaRate, NovaSales, NovaQuote, NovaBook และอื่น ๆ) ทำใบเสนอราคาอัตโนมัติ จองระวาง อ่านเอกสาร OCR และคิดค่าใช้จ่าย ใน AI FMS ของ CargoNova",
    },


  },
  "/pricing": {
    en: {
      title: "CargoNova Pricing | Cloud Platform Subscription for Freight Forwarders",
      description:
        "CargoNova pricing explained: cloud subscription and usage-based plans, cost drivers, and how they compare with CargoWise and legacy TMS licensing.",
    },
    zh: {
      title: "CargoNova 价格计费 | 货代云平台订阅模式",
      description:
        "了解 CargoNova 价格与计费方式：货代云平台订阅制与按用量计费的组合模式、影响价格的核心因素（用户数、单量、模块与集成），以及与 CargoWise 及传统 TMS 授权许可成本的对比，帮助您快速评估总拥有成本。",
    },

    th: {
      title: "CargoNova Pricing | โมเดลค่าใช้จ่ายสำหรับฟอร์วาร์ดเดอร์",
      description:
        "เรียนรู้โมเดล subscription และ usage-based pricing ของ CargoNova ปัจจัยที่มีผลต่อราคา และการเปรียบเทียบกับ legacy TMS",
    },
  },
  "/implementation": {
    en: {
      title: "CargoNova Implementation & Migration | Go-Live in Weeks",
      description:
        "Learn how CargoNova is implemented, how to migrate from CargoWise or legacy TMS, and why phased rollout reduces risk.",
    },
    zh: {
      title: "CargoNova 实施与迁移 | 数周上线指南",
      description:
        "详细了解 CargoNova 的实施路径与上线周期：需求梳理、数据迁移、系统集成、试点验证与全面推广，如何从 CargoWise 或传统 TMS 平滑迁移历史数据，并通过分阶段上线降低业务中断风险，通常数周即可完成首批团队上线。",
    },

    th: {
      title: "CargoNova Implementation | แผน go-live แบบ phased rollout",
      description:
        "เรียนรู้ journey การ implement CargoNova วิธี migrate จาก CargoWise หรือ legacy systems และการลดความเสี่ยงด้วย phased rollout",
    },
  },
  "/privacy": {
    en: {
      title: "Privacy Policy | CargoNova",
      description:
        "CargoNova Privacy Policy — how we collect, use, store and protect your information.",
    },
    zh: {
      title: "隐私政策 | CargoNova",
      description:
        "CargoNova 隐私政策：说明我们在提供国际货代 AI 系统服务过程中如何收集、使用、存储、共享与保护您的个人信息与业务数据，包括 Cookie 使用、数据跨境传输、保存期限，以及您行使访问、更正与删除权利的方式。",
    },

    th: {
      title: "นโยบายความเป็นส่วนตัว | CargoNova",
      description:
        "นโยบายความเป็นส่วนตัวของ CargoNova อธิบายวิธีที่เราเก็บ ใช้ และปกป้องข้อมูลของคุณ",
    },
  },
  "/what-is-cargonova": {
    en: {
      title: "What is CargoNova? AI-Native AI FMS Platform Explained",
      description:
        "What is CargoNova? An AI FMS by Deep Insights — AI-native freight forwarding software where eight AI agents run rates, quoting, booking, documents and settlement.",
    },
    zh: {
      title: "什么是 CargoNova？国际货代 AI 系统（AI FMS）解析",
      description:
        "什么是 CargoNova？CargoNova 是 Deep Insights 打造的国际货代 AI 系统（AI FMS / AI 原生货代平台），由 8 位货代 AI 智能体覆盖运价、智能报价、订舱、单证识别、关务申报与费用结算全流程，帮助货代企业替代重复人工、缩短响应时间并降低运营成本。",
    },
    th: {
      title: "CargoNova คืออะไร? แพลตฟอร์ม AI FMS แบบ AI-Native",
      description:
        "CargoNova คืออะไร? คือ AI FMS (ระบบบริหารงานขนส่งด้วย AI) จาก Deep Insights ซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบ AI-Native พร้อม AI Agent 8 ตัวครบทุกกระบวนการ",
    },
  },

  "/features": {
    en: {
      title: "AI Freight Management System Features | CargoNova",
      description:
        "CargoNova AI freight management system features: rate management, AI quoting, booking and tracking, document AI, customs compliance and automated settlement for ocean, air and cross-border trucking.",
    },
    zh: {
      title: "国际货代 AI 系统功能全览 | CargoNova 平台核心能力",
      description:
        "CargoNova 国际货代 AI 系统（AI FMS）六大核心功能：运价管理、AI 智能报价、订舱与轨迹跟踪、单证智能识别、关务合规与自动结算利润分析，覆盖海运货代系统、空运货代系统与跨境陆运场景，并与传统货代软件（legacy TMS）逐项对比。",
    },
    th: {
      title: "ฟีเจอร์ระบบบริหารงานขนส่งด้วย AI | CargoNova",
      description:
        "ฟีเจอร์ของ CargoNova: การจัดการเรท ใบเสนอราคาด้วย AI การจองและติดตาม AI อ่านเอกสาร ศุลกากร และการชำระเงินอัตโนมัติ ครอบคลุมทางเรือ ทางอากาศและรถข้ามแดน",
    },
  },



  "/compare/cargonova-vs-cargowise": {
    en: {
      title: "CargoWise Alternative: CargoNova vs CargoWise Comparison",
      description:
        "Compare CargoNova and CargoWise on pricing, implementation, UX and support — AI-native freight forwarding software vs legacy TMS.",
    },
    zh: {
      title: "CargoWise 替代方案 | CargoNova 与 CargoWise 对比选型",
      description:
        "正在寻找 CargoWise 替代方案？本文逐项对比 CargoNova 与 CargoWise 的价格与计费模式、实施周期、使用体验、系统集成能力与服务支持，说明 AI 原生的国际货代 AI 系统与传统货代软件（legacy TMS）在成本与效率上的核心差异，帮助您完成选型决策。",
    },

    th: {
      title: "ทางเลือกแทน CargoWise | CargoNova vs CargoWise",
      description:
        "กำลังมองหาทางเลือกแทน CargoWise? เปรียบเทียบ CargoNova และ CargoWise ทั้งราคา การ implement ประสบการณ์ใช้งาน การเชื่อมต่อ และการซัพพอร์ต",
    },

  },
  "/blog/ai-vs-rpa-logistics": {
    ogType: "article",
    en: {
      title: "AI Agents vs. RPA in Logistics | CargoNova",
      description:
        "AI agents vs. traditional RPA in freight forwarding: adaptability, maintenance cost, end-to-end ROI, and how CargoNova avoids the RPA maintenance trap.",
    },
    zh: {
      title: "AI Agent vs. 传统 RPA | CargoNova",
      description:
        "深度对比 AI Native AI Agent 与传统 RPA 在货代场景的差异：自适应性、维护成本、端到端 ROI，以及 CargoNova 如何规避 RPA 维护陷阱。",
    },
    th: {
      title: "AI Agent เทียบกับ RPA ในงานโลจิสติกส์ | CargoNova",
      description:
        "เปรียบเทียบ AI Agent แบบ AI-Native กับ RPA แบบดั้งเดิมในงานฟอร์เวิร์ดเดอร์: ความยืดหยุ่น ต้นทุนดูแลรักษา และ ROI แบบ end-to-end",
    },
  },
  "/blog/what-is-ai-fms": {
    ogType: "article",
    en: {
      title: "What Is an FMS System? Freight Management System Explained",
      description:
        "FMS means freight management system. An AI FMS platform is an AI-native FMS system where AI agents run quoting, booking, documents, customs and settlement.",
    },
    zh: {
      title: "什么是 FMS 系统（货运管理系统）/ AI FMS 平台？",
      description:
        "FMS 系统即货运管理系统（freight management system）；AI FMS 平台由 AI Agent 完成报价、订舱、单证、关务与结算，是 AI 原生的 FMS 系统。本文对比传统 TMS 并给出选型清单。",
    },
    th: {
      title: "ระบบ FMS (Freight Management System) คืออะไร?",
      description:
        "FMS คือระบบบริหารงานขนส่ง (freight management system) ส่วน AI FMS คือแพลตฟอร์ม FMS แบบ AI-native ที่ AI Agent ทำใบเสนอราคา จองระวาง เอกสาร ศุลกากร และการชำระเงิน",
    },
  },


  "/blog/roi-of-ai-agents-freight-forwarding": {

    ogType: "article",
    en: {
      title: "ROI of AI Agents in Freight Forwarding | CargoNova",
      description:
        "The financial case for AI agents in freight forwarding: cost per shipment vs. manual and RPA, payback window, ROI drivers, and a buyer's checklist.",
    },
    zh: {
      title: "AI Agent 货代 ROI 深度指南 | CargoNova",
      description:
        "拆解 AI Agent 在国际货代场景的经济账：与手工、RPA 的单票成本对比、回本周期、直接与间接 ROI，以及采购前的检查清单。",
    },
    th: {
      title: "ROI ของ AI Agent ในงานฟอร์เวิร์ดเดอร์ | CargoNova",
      description:
        "มุมมองด้านการเงินของการใช้ AI Agent: ต้นทุนต่อชิปเมนต์เทียบกับงานมือและ RPA ระยะเวลาคืนทุน และเช็กลิสต์ก่อนตัดสินใจ",
    },
  },
};

const localeMap: Record<SeoLang, string> = {
  en: "en_US",
  zh: "zh_CN",
  th: "th_TH",
};

export const seoUrl = (base: string, lang: SeoLang) => {
  if (base === "/") {
    return lang === "en" ? `${SITE}/` : `${SITE}/${lang}`;
  }
  return lang === "en" ? `${SITE}${base}` : `${SITE}/${lang}${base}`;
};

/** Builds the TanStack `head()` payload for a route. */
export const pageHead = (base: string, lang: SeoLang) => {
  const entry = PAGE_SEO[base];
  if (!entry) return {};
  const { title, description } = entry[lang];
  const url = seoUrl(base, lang);
  const ogType = entry.ogType ?? "website";

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: ogType },
      { property: "og:url", content: url },
      { property: "og:locale", content: localeMap[lang] },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [
      { rel: "canonical", href: url },
      { rel: "alternate", hrefLang: "en", href: seoUrl(base, "en") },
      { rel: "alternate", hrefLang: "zh-CN", href: seoUrl(base, "zh") },
      { rel: "alternate", hrefLang: "zh", href: seoUrl(base, "zh") },
      { rel: "alternate", hrefLang: "th-TH", href: seoUrl(base, "th") },
      { rel: "alternate", hrefLang: "x-default", href: seoUrl(base, "en") },
    ],
  };
};
